/**
 * O item que as peças do cinema desenham.
 *
 * O Flutter tem um modelo só — `MediaItem` — que serve tanto para o que veio do
 * TMDB quanto para o que está guardado no armazém. Aqui são dois tipos
 * diferentes (`ItemMidia` e `DocMidia`), então este arquivo é a ponte: as
 * fileiras, os cartazes e o cartaz gigante recebem sempre um `Cartaz`, venha
 * ele de onde vier.
 *
 * Mora no compartilhado porque o site e o celular desenham as mesmas peças de
 * cinema, e a ponte entre os dois tipos é a mesma nos dois lugares.
 */

import { fundoDoDoc, posterDoDoc } from '../types/cinema';
import type { DocMidia } from '../types/cinema';
import type { ItemMidia, TipoMidia } from '../api/tmdb';

export interface Cartaz {
  tmdbId: number;
  titulo: string;
  tipo: TipoMidia;
  /** URL inteira, ou vazio quando o TMDB não tem imagem. */
  poster: string;
  fundo: string;
  ano: string;
  sinopse: string;
  nota: number;
  anime: boolean;
  /**
   * De qual catálogo o `tmdbId` veio: `tmdb` ou `jikan`.
   *
   * Não é o mesmo que `anime`. `anime` é gênero — um título do TMDB marcado
   * como animação japonesa continua tendo id do TMDB. `fonte === 'jikan'` é o
   * que o Flutter chama de anime no tocador: quer dizer que o número guardado
   * em `tmdbId` é na verdade um id do MAL e precisa ser cruzado antes de virar
   * endereço de vídeo.
   */
  fonte: string;
  /** Presente só quando o item veio do armazém. */
  doc?: string;
  status?: string;
  quem?: string;
  temporada?: number;
  episodio?: number;
}

export const doTMDB = (m: ItemMidia): Cartaz => ({
  tmdbId: m.tmdbId,
  titulo: m.titulo,
  tipo: m.tipo,
  poster: m.poster,
  fundo: m.fundo,
  ano: m.ano,
  sinopse: m.sinopse,
  nota: m.nota,
  anime: m.anime,
  /* O que a busca do TMDB devolve tem sempre id do TMDB, mesmo quando é anime:
     o `ItemMidia` nem carrega esse campo. */
  fonte: 'tmdb',
});

export const doArmazem = (id: string, d: DocMidia): Cartaz => ({
  tmdbId: d.tmdbId,
  titulo: d.title,
  tipo: d.mediaType,
  poster: posterDoDoc(d.posterPath),
  fundo: fundoDoDoc(d.backdropPath),
  ano: d.year,
  sinopse: d.synopsis ?? '',
  nota: d.score ?? 0,
  anime: d.isAnime === true,
  fonte: d.source,
  doc: id,
  status: d.status,
  quem: d.userName,
  temporada: d.currentSeason,
  episodio: d.currentEpisode,
});

/** `_continueProgress` da aba Início. */
export function progressoDe(c: Cartaz): number {
  if (c.tipo !== 'tv') return 0.12;
  return (((c.episodio ?? 1) - 1) % 12) / 12;
}

/** `_continueSubtitle` da aba Início. */
export function legendaDe(c: Cartaz): string {
  if (c.tipo === 'tv' && c.temporada != null) {
    return `T${c.temporada} · E${c.episodio ?? 1}`;
  }
  return 'Continuar assistindo';
}

/** O ano que o TMDB devolve vem inteiro ou vazio; aqui vira número ou nulo. */
export const anoDe = (c: Cartaz): number | null => {
  const n = Number.parseInt(c.ano, 10);
  return Number.isFinite(n) ? n : null;
};
